/**
 * ENG-9207: in-app upgrade CTAs open the plan chooser (/dashboard/billing/plans) with a `?from=`
 * naming the surface, and a subscriber who follows one stays on the chooser instead of being
 * sent to their subscription page (which has no AI Dashboards or upgrade offer). The chooser
 * already shows each user the right option: the add-on toggle for single-seat Pro, the Pro +
 * AI Dashboards bundle for no-subscription / Starter, contact sales for team plans.
 *
 * No imports so this module can be unit-tested without ts-jest hitting ESM-only dependencies.
 */

export const PLAN_CHOOSER_PATH = "/dashboard/billing/plans";

// "Change plan" on the subscription and billing-history pages. The chooser's Back button is
// shown only for this source: from anywhere else the chooser was opened in a new tab, so
// history.goBack() would have nowhere to go.
export const BILLING_PAGE_SOURCE = "billing";

export type WebDashboardUpgradeModalVariant = "expired" | "proactive" | "trial_info";

// The in-dashboard upgrade modal, one source per variant (each variant has one entry point:
// expired = "Dashboard trial expired" banner Upgrade, proactive = nav bar trial badge,
// trial_info = trial-start banner Learn More).
export const WEB_DASHBOARD_UPGRADE_MODAL_SOURCES: Record<WebDashboardUpgradeModalVariant, string> =
  {
    expired: "web_dashboard_upgrade_modal_expired",
    proactive: "web_dashboard_upgrade_modal_proactive",
    trial_info: "web_dashboard_upgrade_modal_trial_info",
  };

// AI Dashboards upgrade CTAs (webapp upgrade modal / banners, Sheets trial banners). Keep the
// Sheets values in sync with TRIAL_*_BILLING_FROM in gsheets-addon WebDashboardTrialBanner.tsx.
export const AI_DASHBOARDS_UPGRADE_SOURCES: ReadonlySet<string> = new Set([
  ...Object.values(WEB_DASHBOARD_UPGRADE_MODAL_SOURCES),
  "sheets_web_dashboard_trial_countdown",
  "sheets_web_dashboard_trial_expired",
]);

/** Whether the billing page was opened from an AI Dashboards upgrade CTA. */
export const isAiDashboardsUpgradeSource = (from: string | null | undefined): boolean =>
  Boolean(from) && AI_DASHBOARDS_UPGRADE_SOURCES.has(from as string);

// Every in-app gate whose CTA means "upgrade / buy more" and lands on the plan chooser. A
// subscriber arriving from one of these stays on the chooser. Deliberately an allowlist rather
// than "any non-empty from": the Sheets add-on's getBillingUrl always sets `from`, including for
// "manage my billing" entry points (hamburger menu, payment_failed_message) where the
// subscription page is the right landing, and email links carry their own `from` values.
export const PLAN_CHOOSER_UPGRADE_SOURCES: ReadonlySet<string> = new Set([
  ...AI_DASHBOARDS_UPGRADE_SOURCES,
  // Sheets add-on gates (gsheets-addon OpenBillingPageFrom / getBillingUrl callers).
  "import_detail_run_refresh_limit_modal",
  "import_run_limit_reached_popup",
  "upgrade_to_pro_popup",
  "upgrade_to_enterprise_popup",
  "scheduler_upgrade_to_pro",
  "snapshot_upgrade_to_pro",
  "webhook_upgrade_to_pro",
  "ai_usage_limit_reached",
  "pro_trial_ended_popup",
]);

export const SUBSCRIPTION_PATH = "/dashboard/billing/subscription";

// Query param that opens the AI Dashboards "Confirm plan changes" modal on the subscription page.
export const ADD_AI_DASHBOARDS_PARAM = "add_ai_dashboards";

/**
 * Where an in-app AI Dashboards upgrade CTA sends the user, from the self-serve offer
 * (WebDashboardEligibility values; strings so this module stays import-free):
 * - "eligible" (single-seat Pro without the add-on): the subscription page with the confirm
 *   modal already open - the plan chooser's toggle is easy to miss for a subscriber.
 * - anything else: the plan chooser. Bundle buyers get the add-on pre-selected; ineligible
 *   users (over the dashboard limit, team plans, ...) get contact sales there, from the AI
 *   Dashboards toggle or Manage Licenses (Oct 22 mocks, flow 3).
 */
export type WebDashboardUpgradeDestination = "add_on_modal" | "plan_chooser";

export const getWebDashboardUpgradeDestination = (
  eligibility: string | null | undefined
): WebDashboardUpgradeDestination => (eligibility === "eligible" ? "add_on_modal" : "plan_chooser");

/** The subscription page with the AI Dashboards confirm modal open, tagged with its source. */
export const getAddAiDashboardsUrl = (from: string): string =>
  `${SUBSCRIPTION_PATH}?${new URLSearchParams({
    from,
    [ADD_AI_DASHBOARDS_PARAM]: "1",
  }).toString()}`;

/** The plan chooser URL for an in-app upgrade CTA, tagged with the surface it came from. */
export const getPlanChooserUrl = (from: string): string =>
  `${PLAN_CHOOSER_PATH}?${new URLSearchParams({ from }).toString()}`;

/**
 * Whether a user who opened the plan chooser with this `?from=` stays on it rather than being
 * redirected to their subscription page (subscribers) or the subscriptions list (users without
 * a subscription of their own). "billing" (Change plan) always stays, as before; the in-app
 * upgrade sources stay only with self-serve AI Dashboards on, so the flag off keeps today's
 * redirects.
 */
export const shouldStayOnPlanChooser = (
  from: string | null | undefined,
  selfServeAiDashboardsEnabled: boolean,
  // Subscribers only: whether they manage the plan. The chooser checks out plan changes on the
  // team's subscription, so a seat holder who isn't a plan manager is sent to their subscription
  // page instead. "billing" (Change plan) needs no check - it shows non-managers the manager
  // prompt before linking here.
  canManagePlan = true
): boolean => {
  if (!from) {
    return false;
  }
  if (from === BILLING_PAGE_SOURCE) {
    return true;
  }
  return selfServeAiDashboardsEnabled && canManagePlan && PLAN_CHOOSER_UPGRADE_SOURCES.has(from);
};

// Eligibilities (WebDashboardEligibility values) whose AI Dashboards upgrade goes through sales.
const AI_DASHBOARDS_CONTACT_SALES_ELIGIBILITIES: ReadonlySet<string> = new Set([
  "multi_seat",
  "sales_managed",
  "domain_has_dashboard_pack",
  "domain_has_other_subscriptions",
  "too_many_dashboards",
  "open_sales_deal",
]);

export type SubscriptionPageAiDashboardsPrompt = "add_on_modal" | "ask_manager" | "contact_sales";

/**
 * What the subscription page opens for someone who followed an AI Dashboards upgrade CTA there
 * (the `?add_ai_dashboards` link, or a seat holder redirected off the plan chooser):
 * - an add-on-eligible plan: the confirm modal for its plan manager, an "ask your plan manager"
 *   prompt for a seat holder who can't change the plan;
 * - a plan that can't self-serve the add-on: contact sales;
 * - anything else (already has it, gate off, ...): nothing.
 */
export const getSubscriptionPageAiDashboardsPrompt = ({
  from,
  hasAddParam,
  eligibility,
  isManager,
}: {
  from: string | null | undefined;
  hasAddParam: boolean;
  eligibility: string | null | undefined;
  isManager: boolean;
}): SubscriptionPageAiDashboardsPrompt | null => {
  if (!hasAddParam && !isAiDashboardsUpgradeSource(from)) {
    return null;
  }
  if (eligibility === "eligible") {
    return isManager ? "add_on_modal" : "ask_manager";
  }
  if (eligibility && AI_DASHBOARDS_CONTACT_SALES_ELIGIBILITIES.has(eligibility)) {
    return "contact_sales";
  }
  return null;
};
